/** How somebody is attached to a fixture — a jockey, an umpire, a captain. */

import { Link } from "react-router-dom";

import { IconPlus } from "@/components/Icons";
import { Button, Card, Empty, PageHead, Pill, Problem, Table, Td, Th } from "@/components/kit";
import { listRoles } from "@/lib/api/client";
import { useApi } from "@/spa/useApi";

/** How the categories read to somebody who did not name them. */
const MEANS: Record<string, string> = {
  COMPOSITION: "someone a side fields",
  CONNECTION: "someone attached to an entrant",
  OFFICIAL: "someone attached to the fixture",
};

const ADD = (
  <Button tone="primary">
    <Link
      to="/roles/new"
      style={{
        color: "inherit",
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--s2)",
      }}
    >
      <IconPlus size={15} />
      Add a role
    </Link>
  </Button>
);

export function RolesPage() {
  const { data: rows, loading, error } = useApi(listRoles, [], "failed to load");

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;
  if (!rows) return <PageHead title="Roles" note={loading ? "Loading…" : ""} actions={ADD} />;

  return (
    <>
      <PageHead
        title="Roles"
        note="How somebody is attached to a fixture — a jockey, an umpire, a captain."
        actions={ADD}
      />

      {rows.length === 0 ? (
        <Card>
          <Empty
            title="No roles yet"
            note="Publishing a sport creates the roles its fixture kinds name, so this usually fills itself."
            action={ADD}
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Code</Th>
              <Th>What it means</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id ?? r.code}>
                <Td strong>{r.name}</Td>
                <Td quiet>{r.code}</Td>
                <Td>
                  <Pill>{MEANS[r.category] ?? r.category}</Pill>
                </Td>
                <Td>
                  {r.id && (
                    <Link to={`/roles/${r.id}/edit`} style={{ fontSize: "var(--t-sm)" }}>
                      Edit
                    </Link>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
